import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { GroupDetails } from '@/types/groups';
import { useUpdateGroupNameMutation } from '@/hooks/queries/groups';
import { dedupError, dedupSuccess, makeUpdateToastId } from '@/lib/toast';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import GroupSidebarItemInfo from '@/components/groups/GroupSidebarItemInfo';

type GroupSidebarItemNameProps = {
  group: GroupDetails;
  isEditing: boolean;
  onEditingChange: (isEditing: boolean) => void;
};

/**
 * Inline group name in the sidebar — switches to an input when renaming via context menu.
 */
function GroupSidebarItemName({
  group,
  isEditing,
  onEditingChange,
}: GroupSidebarItemNameProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draftName, setDraftName] = useState(group.name);
  const { mutateAsync: updateGroupName, isPending: isUpdatingName } =
    useUpdateGroupNameMutation();

  useEffect(() => {
    if (!isEditing) {
      setDraftName(group.name);
    }
  }, [group.name, isEditing]);

  useEffect(() => {
    if (!isEditing || !inputRef.current) return;
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [isEditing]);

  const handleCancelRename = useCallback(() => {
    if (isUpdatingName) return;
    setDraftName(group.name);
    onEditingChange(false);
  }, [group.name, isUpdatingName, onEditingChange]);

  const handleCommitRename = useCallback(async () => {
    if (isUpdatingName) return;

    const trimmedName = draftName.trim();
    if (!trimmedName) {
      toast.error('Group name cannot be empty');
      return;
    }

    if (trimmedName === group.name.trim()) {
      handleCancelRename();
      return;
    }

    const groupId = String(group.id);
    const toastId = makeUpdateToastId('group', groupId, 'name');

    try {
      const response = await updateGroupName({ groupId, name: trimmedName });
      if (response.status) {
        dedupSuccess(toastId, 'Group renamed');
        onEditingChange(false);
      } else {
        dedupError(toastId, 'Failed to rename group');
      }
    } catch (error: unknown) {
      dedupError(
        toastId,
        error instanceof Error && error.message ? error.message : 'Failed to rename group',
      );
    }
  }, [
    draftName,
    group.id,
    group.name,
    handleCancelRename,
    isUpdatingName,
    onEditingChange,
    updateGroupName,
  ]);

  if (!isEditing) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <Tooltip delayDuration={150}>
          <TooltipTrigger asChild>
            <span className="min-w-0 truncate font-medium">
              {group.name}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="max-w-[280px]">
            <p className="text-sm wrap-break-word">{group.name}</p>
          </TooltipContent>
        </Tooltip>
        {/* <GroupSidebarItemInfo group={group} /> */}
      </div>
    );
  }

  return (
    <div
      className="flex min-w-0 flex-1 items-center gap-2"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={draftName}
        disabled={isUpdatingName}
        onChange={(event) => setDraftName(event.target.value)}
        onBlur={() => {
          void handleCommitRename();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.preventDefault();
            void handleCommitRename();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            handleCancelRename();
          }
        }}
        className={cn(
          'border-primary-p-main h-8 min-w-0 flex-1 rounded-sm border bg-background px-2 text-sm outline-none',
          isUpdatingName && 'opacity-70',
        )}
        aria-label="Rename group"
      />
      {isUpdatingName ? (
        <Loader2 className="text-muted-foreground h-4 w-4 shrink-0 animate-spin" />
      ) : null}
    </div>
  );
}

export default memo(GroupSidebarItemName);
